import React, { useState, useEffect, useCallback } from 'react'
import { Table, Button, Space, Modal, Tag, Tooltip, Popconfirm, Badge, Card, Alert, Input, Select, Row, Col, Form, message } from 'antd'
import { PlusOutlined, DeleteOutlined, EyeOutlined, ExclamationCircleOutlined, ClockCircleOutlined, CheckCircleOutlined, SearchOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons'
import { useNavigate, Link } from 'react-router-dom'
import { useStateContext } from '../../../../../contexts/ContextProvider'
import { usePermissions } from '../../../../../hooks/usePermissions'
import { PERMISSIONS, ROLES } from '../../../../../config/permissions'
import { App } from 'antd'

function OferteLogistica() {
  const { message: antMessage } = App.useApp()
  const { token } = useStateContext()
  const [loading, setLoading] = useState(false)
  const [oferte, setOferte] = useState([])
  const [statusOptions, setStatusOptions] = useState([])
  const [statusLoading, setStatusLoading] = useState({})
  const [filterForm] = Form.useForm()
  const navigate = useNavigate()
  const { checkPermission, getUserRole } = usePermissions()
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  
  // State pentru filtre
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    manager_id: 'all',
    product_status: 'all',
    sort_by: 'date_added',
    sort_order: 'desc',
    client_name: ''
  })

  // Fetch statusuri
  const fetchStatusOptions = useCallback(async () => {
    if (!token) return
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferta-status?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setStatusOptions(data)
      }
    } catch (error) {
      console.error('Eroare la încărcarea statusurilor:', error)
    }
  }, [token])

  // Fetch oferte cu filtru pentru status > 2
  const fetchOferte = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
    if (!token) return
    
    setLoading(true)
    try {
      // Construim parametrii URL
      const params = new URLSearchParams({
        token,
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: 'date_added',
        sort_order: 'desc',
        status: '2,3,4' // Filtru pentru statusurile specifice 2, 3, 4
      })

      // Adăugăm filtrele doar dacă au valori
      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim())
      }
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status)
      }
      if (filters.manager_id && filters.manager_id !== 'all') {
        params.append('manager_id', filters.manager_id.toString())
      }
      if (filters.product_status && filters.product_status !== 'all') {
        params.append('product_status', filters.product_status.toString())
      }
      if (filters.client_name && filters.client_name.trim()) {
        params.append('client_name', filters.client_name.trim())
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/oferte?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        // Filtrăm și pe frontend pentru siguranță - verificăm ca statusul să fie în lista 2,3,4
        const allowedStatuses = [3, 4]
        const filteredOferte = (data.items || []).filter(oferta => allowedStatuses.includes(oferta.status))
        setOferte(filteredOferte)
        setPagination(prev => ({
          ...prev,
          current: data.page || page,
          total: data.total || 0,
          pageSize: data.page_size || pageSize
        }))
      } else {
        let errorMessage = 'Eroare la încărcarea ofertelor'
        try {
          const errorData = await response.json()
          errorMessage = errorData.detail || errorData.message || errorMessage
        } catch (e) {
          // Dacă nu putem parsa JSON-ul, folosim mesajul generic
        }
        console.error('Eroare response oferte:', response.status, errorMessage)
        antMessage.error(errorMessage)
      }
    } catch (error) {
      console.error('Eroare la fetch oferte:', error)
      antMessage.error('Eroare de conexiune la încărcarea ofertelor')
    } finally {
      setLoading(false)
    }
  }, [token, antMessage])

  // Funcție pentru aplicarea filtrelor
  const applyFilters = useCallback((newFilters) => {
    setFilters(newFilters)
    fetchOferte(1, pagination.pageSize, newFilters)
  }, [fetchOferte, pagination.pageSize])

  // Funcție pentru resetarea filtrelor
  const clearFilters = useCallback(() => {
    const emptyFilters = {
      search: '',
      status: 'all',
      manager_id: 'all',
      product_status: 'all',
      client_name: ''
    }
    setFilters(emptyFilters)
    fetchOferte(1, pagination.pageSize, emptyFilters)
  }, [fetchOferte, pagination.pageSize])

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(option => option.id === status)
    return statusOption ? statusOption.color : '#6B7280'
  }

  const getStatusText = (status) => {
    const statusOption = statusOptions.find(option => option.id === status)
    return statusOption ? statusOption.label : status
  }

  // Funcție pentru a verifica dacă oferta are produse noi
  const hasNewProducts = (record) => {
    return record.info?.new_products && record.info.new_products.length > 0
  }

  // Funcție pentru a verifica dacă oferta are produse cu status incomplet
  const hasIncompleteProducts = (record) => {
    return record.produse?.some(produs => produs.product_status_id < 2)
  }

  // Funcție pentru a obține prioritatea ofertei
  const getOfertaPriority = (record) => {
    const hasNew = hasNewProducts(record)
    const hasIncomplete = hasIncompleteProducts(record)
    
    if (hasNew && hasIncomplete) return 'urgent'
    if (hasNew || hasIncomplete) return 'medium'
    return 'normal'
  }

  // Funcție pentru a obține culoarea rândului bazată pe prioritate
  const getRowClassName = (record) => {
    const priority = getOfertaPriority(record)
    switch (priority) {
      case 'urgent':
        return 'urgent-row'
      case 'medium':
        return 'medium-row'
      default:
        return ''
    }
  }

  // Actualizarea statusului unei oferte
  const updateOfertaStatus = useCallback(async (ofertaId, newStatus) => {
    if (!token) {
      antMessage.error('Nu există token de autentificare')
      return false
    }

    const statusExists = statusOptions.find(option => option.id === newStatus)
    if (!statusExists) {
      antMessage.error('Status invalid selectat')
      return false
    }

    setStatusLoading(prev => ({ ...prev, [ofertaId]: true }))

    try {
      // Update optimistic în UI
      setOferte(prev => 
        prev.map(oferta => 
          oferta.id === ofertaId 
            ? { ...oferta, status: newStatus }
            : oferta
        )
      )

      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${ofertaId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      })

      if (response.ok) {
        const statusLabel = statusExists.label
        antMessage.success(`Statusul ofertei a fost schimbat în "${statusLabel}"`)
        
        // Refresh lista pentru a fi siguri că avem datele actualizate
        fetchOferte(pagination.current, pagination.pageSize, filters)
        
        return true
      } else {
        // Rollback optimistic update în caz de eroare
        setOferte(prev => 
          prev.map(oferta => 
            oferta.id === ofertaId 
              ? { ...oferta, status: oferta.status }
              : oferta
          )
        )

        const errorData = await response.json()
        antMessage.error(errorData.detail || 'Eroare la actualizarea statusului')
        return false
      }
    } catch (error) {
      console.error('Eroare la actualizarea statusului:', error)
      
      // Rollback optimistic update
      setOferte(prev => 
        prev.map(oferta => 
          oferta.id === ofertaId 
            ? { ...oferta, status: oferta.status }
            : oferta
        )
      )

      antMessage.error('Eroare de conexiune la actualizarea statusului')
      return false
    } finally {
      setStatusLoading(prev => {
        const newState = { ...prev }
        delete newState[ofertaId]
        return newState
      })
    }
  }, [token, statusOptions, fetchOferte, pagination, filters, antMessage])

  // Șterge ofertă
  const deleteOferta = useCallback(async (id) => {
    if (!token) {
      console.log('No token available')
      return false
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/oferte/${id}?token=${token}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        antMessage.success('Oferta a fost ștearsă cu succes')
        fetchOferte(pagination.current, pagination.pageSize, filters)
        return true
      } else {
        let errorMessage = 'Eroare la ștergerea ofertei'
        
        try {
          const errorData = await response.json()
          
          if (errorData.detail) {
            errorMessage = errorData.detail
          } else if (errorData.message) {
            errorMessage = errorData.message
          } else if (typeof errorData === 'string') {
            errorMessage = errorData
          }
        } catch (jsonError) {
          console.log('Nu s-a putut parsa JSON-ul de eroare:', jsonError)
          switch (response.status) {
            case 400:
              errorMessage = 'Cerere invalidă - verifică datele introduse'
              break
            case 403:
              errorMessage = 'Nu ai permisiunea să ștergi această ofertă'
              break
            case 404:
              errorMessage = 'Oferta nu a fost găsită'
              break
            case 409:
              errorMessage = 'Conflictul: Oferta nu poate fi ștearsă în starea actuală'
              break
            default:
              errorMessage = `Eroare server (${response.status})`
          }
        }
        
        antMessage.error(errorMessage)
        return false
      }
    } catch (error) {
      console.error('Eroare la ștergerea ofertei:', error)
      antMessage.error('Eroare de conexiune')
      return false
    }
  }, [token, fetchOferte, pagination, filters, antMessage])

  const columns = [
    {
      title: 'Ofertă',
      dataIndex: 'id',
      key: 'id',
      width: 160,
      render: (id, record) => {
        const hasNew = hasNewProducts(record)
        const hasIncomplete = hasIncompleteProducts(record)
        const priority = getOfertaPriority(record)
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Tooltip title="Vezi detalii ofertă">
              <Button
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                style={{
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '13px',
                  height: '32px',
                  background: priority === 'urgent' 
                    ? 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)'
                    : priority === 'medium'
                    ? 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)'
                    : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                }}
                onClick={() => navigate(`/dashboard/oferte/${record.id}`)}
              >
                Oferta #{id}
              </Button>
            </Tooltip>
          </div>
        )
      },
    },
    {
      title: 'Client & Livrare',
      key: 'client_info',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
          <Link to={`/dashboard/oferte/client/${record.client_mongo_id}`}>
            <div style={{ fontWeight: '600', color: '#1890ff', marginBottom: '4px' }}>
              {record.client_name || 'Client nespecificat'}
            </div>
          </Link>
          <div style={{ color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
            📍 {record.shiping_city || 'Oraș nespecificat'}
          </div>
          <div style={{ color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {record.amo_id || 'AMO ID nespecificat'}
          </div>
        </div>
      ),
    },
    {
      title: 'Produse',
      key: 'products_info',
      width: 150,
      render: (_, record) => {
        const totalProducts = (record.produse?.length || 0) + (record.info?.new_products?.length || 0)
        const newProducts = record.info?.new_products?.length || 0
        
        return (
          <div style={{ fontSize: '12px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px',
              padding: '4px 8px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px'
            }}>
              <span style={{ fontWeight: '600' }}>Total Produse:</span>
              <span style={{ fontWeight: '600', color: '#1890ff' }}>{totalProducts}</span>
            </div>
            
            {newProducts > 0 && (
              <Tag color="red" style={{ fontSize: '11px', margin: '2px' }}>
                🆕 {newProducts} noi
              </Tag>
            )}
          </div>
        )
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <Tag 
            color={getStatusColor(record.status)} 
            style={{ marginBottom: '8px', fontWeight: '500' }}
          >
            {getStatusText(record.status)}
          </Tag>
          <div style={{ color: '#666' }}>
            📅 {new Date(record.date_added).toLocaleDateString('ro-RO')}
          </div>
        </div>
      ),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Șterge">
            <Popconfirm
              title="Ești sigur că vrei să ștergi această ofertă?"
              onConfirm={() => deleteOferta(record.id)}
              okText="Da"
              cancelText="Nu"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ]

  const handleTableChange = (paginationInfo) => {
    fetchOferte(paginationInfo.current, paginationInfo.pageSize, filters)
  }

  // Funcții pentru gestionarea filtrelor
  const handleFilterChange = (changedFields, allFields) => {
    const formValues = filterForm.getFieldsValue()
    applyFilters(formValues)
  }

  const handleClearFilters = () => {
    filterForm.resetFields()
    clearFilters()
  }

  // Inițializăm form-ul cu valorile curente ale filtrelor
  useEffect(() => {
    filterForm.setFieldsValue(filters)
  }, [filters, filterForm])

  useEffect(() => {
    fetchOferte()
    fetchStatusOptions()
  }, [fetchOferte, fetchStatusOptions])

  return (
    <div className="space-y-4">
      {/* Componenta de filtrare */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FilterOutlined style={{ color: '#1890ff' }} />
            <span>Filtrare Oferte Logistică</span>
          </div>
        }
        size="small" 
        style={{ marginBottom: '16px' }}
      >
        <Form
          form={filterForm}
          layout="vertical"
          onValuesChange={handleFilterChange}
          style={{ marginBottom: 0 }}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item 
                name="search" 
                label="Căutare generală"
                style={{ marginBottom: '8px' }}
              >
                <Input
                  placeholder="ID ofertă, nume client, oraș..."
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item 
                name="status" 
                label="Status ofertă"
                style={{ marginBottom: '8px' }}
              >
                <Select placeholder="Toate statusurile" allowClear>
                  <Select.Option value="all">Toate statusurile</Select.Option>
                  {statusOptions.filter(status => [2, 3, 4].includes(status.id)).map(status => (
                    <Select.Option key={status.status} value={status.id}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div 
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: status.color
                          }}
                        />
                        {status.label}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item 
                name="client_name" 
                label="Nume client"
                style={{ marginBottom: '8px' }}
              >
                <Input
                  placeholder="Caută după nume client..."
                  allowClear
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ paddingTop: '22px' }}>
                <Button 
                  type="default" 
                  icon={<ClearOutlined />} 
                  onClick={handleClearFilters}
                  style={{ marginRight: '8px' }}
                >
                  Resetează filtrele
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Alert pentru ofertele urgente */}
      {oferte.some(oferta => getOfertaPriority(oferta) === 'urgent') && (
        <Alert
          message="Atenție! Există oferte urgente care necesită atenție imediată"
          description="Ofertele marcate cu roșu au atât produse noi de prelucrat, cât și produse cu statusuri incomplete."
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ borderRadius: '8px' }}
        />
      )}

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Oferte pentru Logistică</h2>
          <p className="text-sm text-gray-600 mt-1">
            Afișează ofertele cu statusurile: 3, 4 (contractare si livrare)
          </p>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={oferte}
        loading={loading}
        rowKey="id"
        rowClassName={getRowClassName}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `Afișare ${range[0]}-${range[1]} din ${total} oferte`,
          pageSizeOptions: ['10', '20', '50'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        size="middle"
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden'
        }}
      />

      {/* Stiluri CSS pentru clasele de rând */}
      <style jsx>{`
        .urgent-row {
          background-color: #fff2f0 !important;
          border-left: 4px solid #ff4d4f !important;
        }
        .urgent-row:hover {
          background-color: #ffebe8 !important;
        }
        .medium-row {
          background-color: #fffbf0 !important;
          border-left: 4px solid #faad14 !important;
        }
        .medium-row:hover {
          background-color: #fff7e6 !important;
        }
      `}</style>
    </div>
  )
}

export default OferteLogistica